import React, { useState } from 'react';
import { FileIcon, CalendarIcon, UploadIcon, CheckCircleIcon, ChevronRightIcon } from '../shared/icons';
import styles from './index.module.scss';

const formatAmount = (value) => (value ? value.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '0');

const HeadingChargesAccordion = ({ charges }) => {
  const [open, setOpen] = useState(false);
  if (!charges || charges.length === 0) return null;

  return (
    <div className={styles.chargesAccordion}>
      <button className={styles.chargesToggle} onClick={() => setOpen(o => !o)}>
        <span className={`${styles.chargesChevron} ${open ? styles.open : ''}`}><ChevronRightIcon /></span>
        Charges ({charges.length})
      </button>
      {open && (
        <table className={styles.chargesTable}>
          <thead>
            <tr>
              <th>Charge</th>
              <th>Volume</th>
              <th>Ceiling Price</th>
              <th>Budget (₹)</th>
              <th>Contract Price (₹)</th>
              <th>Budget ($)</th>
              <th>Contract Price ($)</th>
            </tr>
          </thead>
          <tbody>
            {charges.map(charge => (
              <tr key={charge.id}>
                <td>
                  {charge.name || '—'}
                  {charge.isBaseFreight && <span className={styles.baseFreightBadge}>Base Freight</span>}
                </td>
                <td>{charge.volumeValue} {charge.volumeUnitName}</td>
                <td>{formatAmount(charge.ceilingPrice)}</td>
                <td>{formatAmount(charge.budgetInr)}</td>
                <td>{formatAmount(charge.cpInr)}</td>
                <td>{formatAmount(charge.budgetUsd)}</td>
                <td>{formatAmount(charge.cpUsd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const ExpandedDocSection = ({
  rfq, onUploadBookingCopy, onUploadBL, onViewBL,
  onSaveDoc, onDeleteDoc, updateSubmitting, deleteSubmitting,
  pendingDoc, onSavePendingDoc, onDiscardPendingDoc, saveSubmitting,
}) => {
  const { docs, headingCharges } = rfq;
  const hasDocs = docs && docs.length > 0;

  if (!hasDocs && !pendingDoc) {
    return (
      <div className={styles.wrapper}>
        <HeadingChargesAccordion charges={headingCharges} />
        <div className={styles.emptyDocs}>
          No booking documents yet.
          <button className={styles.btnBookingCopy} onClick={() => onUploadBookingCopy(rfq, null)}>
            <UploadIcon size={12} /> Upload Booking Copy
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <HeadingChargesAccordion charges={headingCharges} />
      <table className={styles.subTable}>
        <thead className={styles.subThead}>
          <tr>
            <th>Document Name</th>
            <th>Booking Date</th>
            <th>Booking No</th>
            <th>Product</th>
            <th>Receipt Place</th>
            <th>Loading Port</th>
            <th>Discharge Port</th>
            <th>Source Country</th>
            <th>Destination Country</th>
            <th>Vessel Voyage</th>
            <th>ETD at POL</th>
            <th>Expected delivery</th>
            <th>No of Containers</th>
            <th>Container size</th>
            <th>Container Type</th>
            <th>Vendor name</th>
            <th>Shipping line</th>
            <th>Transshipment Port</th>
            <th>Transshipment ETA</th>
            <th>Transshipment Vessel Voyage</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody className={styles.subTbody}>
          {pendingDoc && (
            <tr className={styles.pendingRow}>
              <td>
                <div className={styles.docName}>
                  <span className={styles.icon}><FileIcon /></span>
                  <span>
                    {pendingDoc.name}
                    <span className={styles.pendingBadge}>OCR Extracted</span>
                  </span>
                </div>
              </td>
              <td>
                <div className={styles.dateCell}>
                  <span className={styles.calIcon}><CalendarIcon size={12} /></span>
                  {pendingDoc.bookingDate || '—'}
                </div>
              </td>
              <td>
                <span className={styles.bookingNoLink}>{pendingDoc.bookingNo || '—'}</span>
              </td>
              <td>
                <span className={styles.productBadge}>{pendingDoc.product || '—'}</span>
              </td>
              <td className={styles.country}>{pendingDoc.receiptPlace || '—'}</td>
              <td className={styles.country}>{pendingDoc.loadingPort || '—'}</td>
              <td className={styles.country}>{pendingDoc.dischargePort || '—'}</td>
              <td className={styles.country}>{pendingDoc.srcCountry || '—'}</td>
              <td className={styles.destCountry}>{pendingDoc.destCountry || '—'}</td>
              <td>
                <span className={styles.vessel}>{pendingDoc.vessel || '—'}</span>
              </td>
              <td>
                <div className={styles.dateCell}>
                  <span className={styles.calIcon}><CalendarIcon size={12} /></span>
                  {pendingDoc.etdAtPol || '—'}
                </div>
              </td>
              <td>
                <div className={styles.dateCell}>
                  <span className={styles.calIcon}><CalendarIcon size={12} /></span>
                  {pendingDoc.expectedDelivery || '—'}
                </div>
              </td>
              <td>{pendingDoc.noOfContainers || '—'}</td>
              <td>{pendingDoc.containerSize || '—'}</td>
              <td>{pendingDoc.containerType || '—'}</td>
              <td>{pendingDoc.vendorName || '—'}</td>
              <td>
                {pendingDoc.shippingLine
                  ? <span className={styles.shippingLineBadge}>{pendingDoc.shippingLine}</span>
                  : '—'}
              </td>
              <td className={styles.country}>{pendingDoc.transshipmentPort || '—'}</td>
              <td>
                <div className={styles.dateCell}>
                  <span className={styles.calIcon}><CalendarIcon size={12} /></span>
                  {pendingDoc.transshipmentEta || '—'}
                </div>
              </td>
              <td>
                <span className={styles.vessel}>{pendingDoc.transshipmentVesselVoyage || '—'}</span>
              </td>
              <td>
                <div className={styles.actions}>
                  <button
                    className={styles.btnSave}
                    onClick={() => onSavePendingDoc && onSavePendingDoc(rfq)}
                    disabled={saveSubmitting}
                  >
                    {saveSubmitting ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    className={styles.btnDelete}
                    onClick={() => onDiscardPendingDoc && onDiscardPendingDoc(rfq)}
                    disabled={saveSubmitting}
                  >
                    Discard
                  </button>
                </div>
              </td>
            </tr>
          )}
          {hasDocs && docs.map(doc => (
            <tr key={doc.id}>
              <td>
                <div className={styles.docName}>
                  <span className={styles.icon}><FileIcon /></span>
                  {doc.name}
                </div>
              </td>
              <td>
                <div className={styles.dateCell}>
                  <span className={styles.calIcon}><CalendarIcon size={12} /></span>
                  {doc.bookingDate}
                </div>
              </td>
              <td>
                <span className={styles.bookingNoLink}>{doc.bookingNo}</span>
              </td>
              <td>
                <span className={styles.productBadge}>{doc.product}</span>
              </td>
              <td className={styles.country}>{doc.receiptPlace}</td>
              <td className={styles.country}>{doc.loadingPort}</td>
              <td className={styles.country}>{doc.dischargePort}</td>
              <td className={styles.country}>{doc.srcCountry}</td>
              <td className={styles.destCountry}>{doc.destCountry}</td>
              <td>
                <span className={styles.vessel}>{doc.vessel}</span>
              </td>
              <td>
                <div className={styles.dateCell}>
                  <span className={styles.calIcon}><CalendarIcon size={12} /></span>
                  {doc.etdAtPol || '—'}
                </div>
              </td>
              <td>
                <div className={styles.dateCell}>
                  <span className={styles.calIcon}><CalendarIcon size={12} /></span>
                  {doc.expectedDelivery || '—'}
                </div>
              </td>
              <td>{doc.noOfContainers || '—'}</td>
              <td>{doc.containerSize || '—'}</td>
              <td>{doc.containerType || '—'}</td>
              <td>{doc.vendorName || '—'}</td>
              <td>
                {doc.shippingLine
                  ? <span className={styles.shippingLineBadge}>{doc.shippingLine}</span>
                  : '—'}
              </td>
              <td className={styles.country}>{doc.transshipmentPort || '—'}</td>
              <td>
                <div className={styles.dateCell}>
                  <span className={styles.calIcon}><CalendarIcon size={12} /></span>
                  {doc.transshipmentEta || '—'}
                </div>
              </td>
              <td>
                <span className={styles.vessel}>{doc.transshipmentVesselVoyage || '—'}</span>
              </td>
              <td>
                <div className={styles.actions}>
                  <button className={styles.btnUploadBL} onClick={() => onUploadBL(rfq, doc)}>
                    <UploadIcon size={12} /> Upload BL
                  </button>
                  {doc.blDocs && doc.blDocs.length > 0 ? (
                    <button className={styles.btnBlUploaded} onClick={() => onViewBL(rfq, doc)}>
                      <CheckCircleIcon /> {doc.blDocs.length} BL{doc.blDocs.length !== 1 ? 's' : ''} Uploaded
                      <span className={styles.blUploadedDivider}>·</span>
                      <FileIcon />
                    </button>
                  ) : (
                    <button className={styles.btnViewBL} onClick={() => onViewBL(rfq, doc)}>
                      View BL
                    </button>
                  )}
                  <button
                    className={styles.btnSave}
                    onClick={() => onSaveDoc && onSaveDoc(rfq, doc)}
                    disabled={updateSubmitting}
                  >
                    {updateSubmitting ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    className={styles.btnDelete}
                    onClick={() => onDeleteDoc && onDeleteDoc(doc)}
                    disabled={deleteSubmitting}
                  >
                    {deleteSubmitting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ExpandedDocSection;
